<template>
	<el-dialog v-dialogDrag class="setting" custom-class="setting-dialog" top="7vh" title="设置" :visible.sync="visible" width="480px" :before-close="onClose" :close-on-click-modal="false" :close-on-press-escape="false">
		<!-- 个人资料头部 -->
		<div class="profile-header">
			<file-upload class="avatar-uploader" :action="imageAction" :showLoading="true" :maxSize="maxSize"
				:isPermanent="true" @success="onUploadSuccess"
				:fileTypes="['image/jpeg', 'image/png', 'image/jpg', 'image/webp']">
				<div class="avatar-box">
					<img v-if="userInfo.headImage" :src="userInfo.headImage" class="avatar">
					<i v-else class="el-icon-plus avatar-uploader-icon"></i>
					<div class="avatar-mask"><i class="el-icon-camera"></i></div>
				</div>
			</file-upload>
			<div class="profile-meta">
				<div class="profile-name">{{ userInfo.nickName || userInfo.userName }}</div>
				<div class="profile-username">@{{ userInfo.userName }}</div>
				<div class="uid-row" v-if="userInfo.uid && !editUid">
					<span class="uid-label">ID</span>
					<span class="uid-val">{{ userInfo.uid }}</span>
					<el-button type="text" icon="el-icon-document-copy" @click="copyUid">复制</el-button>
					<el-button type="text" @click="startEditUid">修改</el-button>
				</div>
				<div class="uid-edit" v-else-if="userInfo.uid">
					<el-input v-model="newUid" size="small" maxlength="16"
						placeholder="字母开头,字母或数字,6-16位"></el-input>
					<el-button type="text" @click="saveUid">保存</el-button>
					<el-button type="text" @click="editUid = false">取消</el-button>
				</div>
				<div class="uid-tip">把ID告诉好友，对方可直接搜索添加你</div>
			</div>
		</div>

		<el-form :model="userInfo" label-width="72px" :rules="rules" ref="settingForm" size="small">
			<!-- 基本资料 -->
			<div class="setting-group">
				<div class="group-title">基本资料</div>
				<el-form-item prop="nickName" label="昵称">
					<el-input v-model="userInfo.nickName" autocomplete="off" size="small" maxlength="20"></el-input>
				</el-form-item>
				<el-form-item label="性别">
					<el-radio-group v-model="userInfo.sex">
						<el-radio :label="0">男</el-radio>
						<el-radio :label="1">女</el-radio>
					</el-radio-group>
				</el-form-item>
				<el-form-item label="个性签名">
					<el-input type="textarea" v-model="userInfo.signature" :rows="3" maxlength="64"
						placeholder="介绍一下自己吧" show-word-limit></el-input>
				</el-form-item>
			</div>

			<!-- 偏好设置 -->
			<div class="setting-group">
				<div class="group-title">偏好设置</div>
				<div class="pref-row">
					<div class="pref-info">
						<div class="pref-name">桌面通知</div>
						<div class="pref-desc">窗口在后台时，新消息弹出浏览器通知</div>
					</div>
					<el-switch v-model="desktopNotification" @change="onSwitchNotification"></el-switch>
				</div>
				<div class="pref-row">
					<div class="pref-info">
						<div class="pref-name">黑夜模式</div>
						<div class="pref-desc">界面切换为深色主题</div>
					</div>
					<el-switch v-model="darkMode" @change="onSwitchDarkMode"></el-switch>
				</div>
			</div>
		</el-form>

		<!-- 临时会话二维码 -->
		<div class="setting-group">
			<div class="group-title">临时会话二维码</div>
			<div class="pref-row">
				<div class="pref-info">
					<div class="pref-name">聊天二维码</div>
					<div class="pref-desc">客户无需下载App,手机浏览器扫码即可直接与你聊天</div>
				</div>
				<el-button size="small" plain @click="toggleGuestQr">{{ showGuestQr ? '收起' : '查看二维码' }}</el-button>
			</div>
			<div class="guest-qr-box" v-if="showGuestQr">
				<div v-if="guestQrLoading" class="guest-qr-loading"><i class="el-icon-loading"></i> 正在生成...</div>
				<template v-else-if="guestQr.qrBase64">
					<img :src="guestQr.qrBase64" class="guest-qr-img" />
					<div class="guest-qr-url">{{ guestQr.url }}</div>
					<div class="guest-qr-actions">
						<el-button size="small" icon="el-icon-document-copy" @click="copyGuestQrUrl">复制链接</el-button>
						<el-button size="small" icon="el-icon-download" @click="downloadGuestQr">下载二维码</el-button>
						<el-button size="small" icon="el-icon-download" :loading="gifExporting" @click="downloadGuestQrGif">下载GIF动图</el-button>
					</div>
					<div class="guest-qr-tip">可印到名片/发朋友圈;GIF动图(黑屏2秒后渐显)可添加到微信表情包。客户需用手机浏览器打开,微信/QQ内扫码会引导去浏览器。</div>
				</template>
			</div>
			<!-- 临时会话欢迎语: 客户首次扫码进入会话时以你的身份自动发送(每客户仅首次) -->
			<div class="pref-row" style="margin-top:14px;border-top:1px dashed var(--im-border-color-light,#ebeef5);padding-top:12px">
				<div class="pref-info">
					<div class="pref-name">临时会话欢迎语</div>
					<div class="pref-desc">客户首次扫码进入会话时,以你的身份自动发送(可多条,按顺序逐条发)</div>
				</div>
				<el-switch v-model="welcomeEnabled" active-text="启用" @change="onWelcomeSwitch"></el-switch>
			</div>
			<div v-if="welcomeEnabled" style="padding:0 4px 8px">
				<div v-if="welcomeLoading" style="color:#999;font-size:13px;padding:8px 0"><i class="el-icon-loading"></i> 加载中...</div>
				<template v-else>
					<div v-for="(line, idx) in welcomeLines" :key="idx" style="display:flex;gap:8px;align-items:flex-start;margin-bottom:10px">
						<span style="flex:0 0 52px;padding-top:8px;font-weight:bold;color:#666">第{{ idx + 1 }}条</span>
						<el-input type="textarea" :rows="3" v-model="welcomeLines[idx]" placeholder="输入这条欢迎语内容(一条内可随意换行,不会被拆开)" style="flex:1"></el-input>
						<el-button size="mini" type="danger" plain icon="el-icon-delete" :disabled="welcomeLines.length <= 1" @click="removeWelcomeLine(idx)" style="margin-top:4px"></el-button>
					</div>
					<div style="display:flex;gap:8px;margin-top:4px">
						<el-button size="small" icon="el-icon-plus" @click="addWelcomeLine">再加一条</el-button>
						<el-button size="small" type="primary" icon="el-icon-check" :loading="welcomeSaving" @click="saveGuestWelcome(false)">保存欢迎语</el-button>
					</div>
				</template>
			</div>
			<!-- 清空临时会话聊天记录(物理删除,客户重进也看不到) -->
			<div class="pref-row" style="margin-top:6px;border-top:1px dashed var(--im-border-color-light,#ebeef5);padding-top:12px">
				<div class="pref-info">
					<div class="pref-name">清空临时会话聊天记录</div>
					<div class="pref-desc">物理删除你与所有扫码客户的聊天记录,不可恢复,客户重新进入也看不到</div>
				</div>
				<el-button size="small" type="danger" plain :loading="clearingGuestChat" @click="clearAllGuestChat">清空全部</el-button>
			</div>
		</div>

		<!-- 安全 -->
		<div class="setting-group">
			<div class="group-title">安全</div>
			<div class="pref-row">
				<div class="pref-info">
					<div class="pref-name">登录密码</div>
					<div class="pref-desc">定期修改密码，保护账号安全</div>
				</div>
				<el-button size="small" plain @click="togglePwd">修改密码</el-button>
			</div>
			<div class="pref-row">
				<div class="pref-info">
					<div class="pref-name">锁屏密码</div>
					<div class="pref-desc">{{ hasLock ? '已开启，离开时自动锁定界面' : '开启后离开时自动锁定界面' }}</div>
				</div>
				<el-button size="small" plain @click="toggleLock">
					{{ hasLock ? '修改/清除' : '设置' }}
				</el-button>
			</div>
			<div class="pref-row">
				<div class="pref-info">
					<div class="pref-name">自动锁屏时间</div>
					<div class="pref-desc">闲置超过此时长后自动锁屏（需已设置锁屏密码）</div>
				</div>
				<el-input-number v-model="idleMinutes" size="small" :min="1" :max="120" :step="1"
					controls-position="right" style="width: 110px" @change="onIdleMinutesChange"></el-input-number>
				<span style="margin-left: 6px; color: #999; font-size: 12px;">分钟</span>
			</div>

		<el-form v-if="showPwd" class="sub-form" :model="pwdForm" :rules="pwdRules" ref="pwdFormRef"
			label-width="90px" size="small">
			<el-form-item label="旧密码" prop="oldPassword">
				<el-input type="password" v-model="pwdForm.oldPassword" show-password autocomplete="off"></el-input>
			</el-form-item>
			<el-form-item label="新密码" prop="newPassword">
				<el-input type="password" v-model="pwdForm.newPassword" show-password autocomplete="off"></el-input>
			</el-form-item>
			<el-form-item label="确认新密码" prop="confirmPassword">
				<el-input type="password" v-model="pwdForm.confirmPassword" show-password autocomplete="off"></el-input>
			</el-form-item>
			<el-form-item>
				<el-button type="primary" size="small" @click="savePwd">保存密码</el-button>
			</el-form-item>
		</el-form>

		<el-form v-if="showLock" class="sub-form" :model="lockForm" :rules="lockRules" ref="lockFormRef"
			label-width="90px" size="small">
			<div class="lock-hint" v-if="hasLock">
				<i class="el-icon-lock"></i> 已设置锁屏密码，可在下方重新设置，或直接清除。
			</div>
			<el-form-item label="锁屏密码" prop="password">
				<el-input type="password" v-model="lockForm.password" show-password autocomplete="off"
					placeholder="至少4位"></el-input>
			</el-form-item>
			<el-form-item label="确认密码" prop="confirmPassword">
				<el-input type="password" v-model="lockForm.confirmPassword" show-password autocomplete="off"></el-input>
			</el-form-item>
			<el-form-item>
				<el-button type="primary" size="small" @click="saveLock">保存锁屏密码</el-button>
				<el-button v-if="hasLock" type="danger" size="small" plain @click="clearLock">清除</el-button>
			</el-form-item>
		</el-form>
		</div>

		<span slot="footer" class="dialog-footer">
			<el-button type="danger" plain @click="onLogout()" style="float: left;">退出登录</el-button>
			<el-button @click="onClose()">取 消</el-button>
			<el-button type="primary" @click="onSubmit()">确 定</el-button>
		</span>
	</el-dialog>
</template>

<script>
import FileUpload from "../common/FileUpload.vue";
import { hasLockPassword, setLockPassword, clearLockPassword, getIdleMinutes, setIdleMinutes } from "../../api/lock.js";
import buildGuestQrGif from "../../utils/guestQrGif.js";

export default {
	name: "setting",
	components: {
		FileUpload
	},
	data() {
		const validateConfirmPwd = (rule, value, callback) => {
			if (value !== this.pwdForm.newPassword) {
				callback(new Error('两次输入的新密码不一致'));
			} else {
				callback();
			}
		};
		const validateLockConfirm = (rule, value, callback) => {
			if (value !== this.lockForm.password) {
				callback(new Error('两次输入的锁屏密码不一致'));
			} else {
				callback();
			}
		};
		return {
			userInfo: {},
			desktopNotification: false,
			darkMode: false,
			editUid: false,
			newUid: '',
			showGuestQr: false,
			guestQrLoading: false,
			gifExporting: false,
			guestQr: {},
			welcomeEnabled: true,
			welcomeLines: [''],
			welcomeLoading: false,
			welcomeSaving: false,
			clearingGuestChat: false,
			showPwd: false,
			showLock: false,
			hasLock: false,
			idleMinutes: 1,
			pwdForm: { oldPassword: '', newPassword: '', confirmPassword: '' },
			lockForm: { password: '', confirmPassword: '' },
			maxSize: 5 * 1024 * 1024,
			action: "/image/upload",
			rules: {
				nickName: [{
					required: true,
					message: '请输入昵称',
					trigger: 'blur'
				}]
			},
			pwdRules: {
				oldPassword: [{ required: true, message: '请输入旧密码', trigger: 'blur' }],
				newPassword: [{ required: true, message: '请输入新密码', trigger: 'blur' },
				{ min: 6, message: '密码至少6位', trigger: 'blur' }],
				confirmPassword: [{ required: true, message: '请再次输入新密码', trigger: 'blur' },
				{ validator: validateConfirmPwd, trigger: 'blur' }]
			},
			lockRules: {
				password: [{ required: true, message: '请输入锁屏密码', trigger: 'blur' },
				{ min: 4, message: '锁屏密码至少4位', trigger: 'blur' }],
				confirmPassword: [{ required: true, message: '请再次输入锁屏密码', trigger: 'blur' },
				{ validator: validateLockConfirm, trigger: 'blur' }]
			}
		}
	},
	methods: {
		onClose() {
			this.$emit("close");
		},
		onSubmit() {
			this.$refs['settingForm'].validate((valid) => {
				if (!valid) {
					return false;
				}
				this.$http({
					url: "/user/update",
					method: "put",
					data: this.userInfo
				}).then(() => {
					this.userStore.setUserInfo(this.userInfo);
					this.$emit("close");
					this.$message.success("修改成功");
				})
			});
		},
		onUploadSuccess(data, file) {
			this.userInfo.headImage = data.originUrl;
			this.userInfo.headImageThumb = data.thumbUrl;
		},
		startEditUid() {
			this.newUid = this.userInfo.uid;
			this.editUid = true;
		},
		toggleGuestQr() {
			this.showGuestQr = !this.showGuestQr;
			if (this.showGuestQr && !this.guestQr.qrBase64 && !this.guestQrLoading) {
				this.loadGuestQr();
			}
		},
		loadGuestQr() {
			this.guestQrLoading = true;
			this.$http({
				url: '/biz/guest-qrcode',
				method: 'get'
			}).then((data) => {
				this.guestQr = data || {};
			}).catch(() => {
				this.$message.error('二维码生成失败');
			}).finally(() => {
				this.guestQrLoading = false;
			});
		},
		copyGuestQrUrl() {
			const text = this.guestQr.url || '';
			if (!text) {
				return;
			}
			if (navigator.clipboard && window.isSecureContext) {
				navigator.clipboard.writeText(text).then(() => {
					this.$message.success('链接已复制');
				}).catch(() => {
					this.fallbackCopy(text);
				});
			} else {
				this.fallbackCopy(text);
			}
		},
		downloadGuestQr() {
			if (!this.guestQr.qrBase64) {
				return;
			}
			const a = document.createElement('a');
			a.href = this.guestQr.qrBase64;
			a.download = '临时会话二维码.png';
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
		},
		// 下载GIF动图: 黑屏2秒 -> 5秒渐显 -> 停留2秒循环, 供业务员添加到微信表情包
		async downloadGuestQrGif() {
			if (!this.guestQr.qrBase64 || this.gifExporting) {
				return;
			}
			this.gifExporting = true;
			try {
				const blob = await buildGuestQrGif(this.guestQr.qrBase64);
				const url = URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = url;
				a.download = '临时会话二维码.gif';
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				URL.revokeObjectURL(url);
				this.$message.success('GIF已下载,可发送到微信后长按添加为表情');
			} catch (e) {
				this.$message.error('GIF生成失败,请重试');
			} finally {
				this.gifExporting = false;
			}
		},
		loadGuestWelcome() {
			this.welcomeLoading = true;
			this.$http({ url: '/biz/guest-welcome', method: 'get' }).then((data) => {
				const d = data || {};
				this.welcomeEnabled = d.enabled !== false;
				const lines = (d.content || '').split('|||').map(s => s.trim()).filter(s => s);
				this.welcomeLines = lines.length ? lines : [''];
			}).catch(() => {
				this.welcomeLines = [''];
			}).finally(() => {
				this.welcomeLoading = false;
			});
		},
		onWelcomeSwitch() {
			// 切换启用开关即持久化(静默保存,不强制校验内容)
			this.saveGuestWelcome(true);
		},
		addWelcomeLine() {
			this.welcomeLines.push('');
		},
		removeWelcomeLine(idx) {
			if (this.welcomeLines.length <= 1) {
				this.welcomeLines = [''];
				return;
			}
			this.welcomeLines.splice(idx, 1);
		},
		saveGuestWelcome(silent) {
			const content = this.welcomeLines.map(s => (s || '').trim()).filter(s => s).join('|||');
			if (this.welcomeEnabled && !content) {
				if (!silent) this.$message.error('请填写至少一条欢迎语,或关闭启用开关');
				return;
			}
			this.welcomeSaving = true;
			this.$http({
				url: '/biz/guest-welcome/save',
				method: 'post',
				data: { content: content, enabled: this.welcomeEnabled }
			}).then(() => {
				if (!silent) this.$message.success('欢迎语已保存');
			}).catch(() => {
				if (!silent) this.$message.error('保存失败');
			}).finally(() => {
				this.welcomeSaving = false;
			});
		},
		clearAllGuestChat() {
			this.$confirm('确定清空你与所有扫码客户的临时会话聊天记录吗？此操作不可恢复，客户重新进入也将看不到这些记录。', '清空确认', {
				confirmButtonText: '确定清空',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				this.clearingGuestChat = true;
				this.$http({ url: '/biz/guest-chat/clear', method: 'post', data: {} }).then((data) => {
					const n = (data && data.clearedConv) || 0;
					this.$message.success('已清空 ' + n + ' 个临时会话的聊天记录');
					// 通知宿主刷新会话列表/清本地缓存
					this.$emit('guest-chat-cleared');
				}).catch(() => {
					this.$message.error('清空失败');
				}).finally(() => {
					this.clearingGuestChat = false;
				});
			}).catch(() => {});
		},
		copyUid() {
			const text = this.userInfo.uid || '';
			if (!text) {
				return;
			}
			if (navigator.clipboard && window.isSecureContext) {
				navigator.clipboard.writeText(text).then(() => {
					this.$message.success('ID已复制');
				}).catch(() => {
					this.fallbackCopy(text);
				});
			} else {
				this.fallbackCopy(text);
			}
		},
		fallbackCopy(text) {
			try {
				const input = document.createElement('textarea');
				input.value = text;
				input.style.position = 'fixed';
				input.style.opacity = '0';
				document.body.appendChild(input);
				input.select();
				document.execCommand('copy');
				document.body.removeChild(input);
				this.$message.success('ID已复制');
			} catch (e) {
				this.$message.error('复制失败，请手动复制');
			}
		},
		saveUid() {
			const uid = (this.newUid || '').trim();
			if (!/^[a-zA-Z][a-zA-Z0-9]{5,15}$/.test(uid)) {
				this.$message.error("ID需字母开头，由字母或数字组成，6-16位");
				return;
			}
			if (uid === this.userInfo.uid) {
				this.editUid = false;
				return;
			}
			this.$http({
				url: "/user/modifyUid",
				method: "put",
				params: { uid }
			}).then(() => {
				this.userInfo.uid = uid;
				const mine = JSON.parse(JSON.stringify(this.userStore.userInfo));
				mine.uid = uid;
				this.userStore.setUserInfo(mine);
				this.editUid = false;
				this.$message.success("ID修改成功");
			})
		},
		togglePwd() {
			this.showPwd = !this.showPwd;
			if (this.showPwd) {
				this.showLock = false;
				this.pwdForm = { oldPassword: '', newPassword: '', confirmPassword: '' };
			}
		},
		savePwd() {
			this.$refs['pwdFormRef'].validate((valid) => {
				if (!valid) {
					return false;
				}
				this.$http({
					url: "/modifyPwd",
					method: "put",
					data: {
						oldPassword: this.pwdForm.oldPassword,
						newPassword: this.pwdForm.newPassword
					}
				}).then(() => {
					this.showPwd = false;
					this.$message.success("密码修改成功");
				})
			});
		},
		toggleLock() {
			this.showLock = !this.showLock;
			if (this.showLock) {
				this.showPwd = false;
				this.hasLock = hasLockPassword(this.userStore.userInfo.id);
				this.lockForm = { password: '', confirmPassword: '' };
			}
		},
		saveLock() {
			this.$refs['lockFormRef'].validate(async (valid) => {
				if (!valid) {
					return false;
				}
				await setLockPassword(this.userStore.userInfo.id, this.lockForm.password);
				this.hasLock = true;
				this.showLock = false;
				this.$message.success("锁屏密码已保存");
			});
		},
		clearLock() {
			this.$confirm('确定清除锁屏密码吗？清除后将不再自动锁屏。', '提示', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				clearLockPassword(this.userStore.userInfo.id);
				this.hasLock = false;
				this.showLock = false;
				this.$message.success("锁屏密码已清除");
			}).catch(() => { });
		},
		onIdleMinutesChange(val) {
			setIdleMinutes(this.userStore.userInfo.id, val);
			this.$message.success(`闲置锁屏时间已设为 ${val} 分钟`);
		},
		onSwitchDarkMode(val) {
			this.configStore.setDarkMode(val);
		},
		onSwitchNotification(val) {
			if (val) {
				if (!("Notification" in window)) {
					this.$message.error("当前浏览器不支持桌面通知");
					this.desktopNotification = false;
					return;
				}
				Notification.requestPermission().then((permission) => {
					if (permission == "granted") {
						localStorage.setItem("desktopNotification", "true");
						this.$message.success("已开启桌面通知");
					} else {
						this.desktopNotification = false;
						localStorage.setItem("desktopNotification", "false");
						this.$message.warning("浏览器通知权限未开启，请在浏览器设置中允许通知");
					}
				});
			} else {
				localStorage.setItem("desktopNotification", "false");
			}
		},
		onLogout() {
			this.$confirm('确定退出登录吗？', '退出登录', {
				confirmButtonText: '退出',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				this.$emit("logout");
			}).catch(() => { });
		}
	},
	props: {
		visible: {
			type: Boolean
		}
	},
	computed: {
		imageAction() {
			return `/image/upload?thumbSize=20`;
		}
	},
	watch: {
		visible: function () {
			// 深拷贝
			let mine = this.userStore.userInfo;
			this.userInfo = JSON.parse(JSON.stringify(mine));
			// 同步桌面通知开关状态
			this.desktopNotification = localStorage.getItem("desktopNotification") == "true"
				&& ("Notification" in window) && Notification.permission == "granted";
			// 同步黑夜模式开关
			this.darkMode = this.configStore.darkMode;
			// 同步锁屏密码状态
			this.hasLock = hasLockPassword(this.userStore.userInfo.id);
			// 同步锁屏闲置时间
			this.idleMinutes = getIdleMinutes(this.userStore.userInfo.id);
			// 复位子表单
			this.editUid = false;
			this.showPwd = false;
			this.showLock = false;
			// 复位并加载临时会话欢迎语
			this.welcomeLines = [''];
			this.welcomeEnabled = true;
			this.loadGuestWelcome();
		}
	}
}
</script>

<style lang="scss" scoped>
.setting {
	// 个人资料头部
	.profile-header {
		display: flex;
		align-items: center;
		gap: 16px;
		padding: 4px 4px 16px;
		margin-bottom: 4px;
		border-bottom: 1px solid var(--im-border-color-light, #ebeef5);

		.profile-meta {
			flex: 1;
			min-width: 0;

			.profile-name {
				font-size: 18px;
				font-weight: 600;
				color: var(--im-text-color);
				line-height: 1.4;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
		}

			.profile-username {
				margin-top: 2px;
				font-size: var(--im-font-size-smaller);
				color: var(--im-text-color-light);
			}

			.uid-tip {
				margin-top: 6px;
				font-size: var(--im-font-size-smaller);
				color: var(--im-text-color-light);
			}
		}
	}

	.uid-row {
		display: flex;
		align-items: center;
		gap: 6px;
		margin-top: 6px;

		.uid-label {
			padding: 0 6px;
			border-radius: 4px;
			font-size: 12px;
			background: var(--im-color-primary-light-9, #ecf5ff);
			color: var(--im-color-primary, #409EFF);
		}

		.uid-val {
			font-weight: 600;
			color: var(--im-text-color);
		}

		.el-button {
			padding: 0 2px;
		}
	}

	.uid-edit {
		display: flex;
		align-items: center;
		gap: 6px;
		margin-top: 6px;
	}

	// 分组卡片
	.setting-group {
		margin-top: 16px;

		.group-title {
			position: relative;
			padding-left: 10px;
			margin-bottom: 10px;
			font-size: 14px;
			font-weight: 600;
			color: var(--im-text-color);

			&::before {
				content: "";
				position: absolute;
				left: 0;
				top: 50%;
				transform: translateY(-50%);
				width: 3px;
				height: 14px;
				border-radius: 2px;
				background: var(--im-color-primary, #409EFF);
			}
		}

		.el-form-item {
			margin-bottom: 14px;
		}
	}

	// 偏好/安全 开关行
	.pref-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		padding: 10px 0;

		& + .pref-row {
			border-top: 1px solid var(--im-border-color-light, #f2f2f2);
		}

		.pref-info {
			flex: 1;
			min-width: 0;

			.pref-name {
				font-size: 14px;
				color: var(--im-text-color);
				line-height: 1.5;
			}

			.pref-desc {
				margin-top: 2px;
				font-size: var(--im-font-size-smaller);
				color: var(--im-text-color-light);
			}
		}
	}

	.sub-form {
		margin-top: 8px;
		padding: 12px 12px 4px;
		border-radius: 8px;
		background: var(--im-fill-color-light, #f5f7fa);
	}

	.lock-hint {
		padding: 0 0 10px 2px;
		font-size: var(--im-font-size-smaller);
		color: var(--im-text-color-light);
	}

	.avatar-uploader {
		--width: 72px;
		display: inline-block;
		cursor: pointer;

		// 用独立盒子承载头像与相机遮罩，保证遮罩始终限定在头像内，不会溢出到弹窗其它区域
		.avatar-box {
			position: relative;
			width: var(--width);
			height: var(--width);
			border: 1px dashed #d9d9d9;
			border-radius: 50%;
			overflow: hidden;
		}

		&:hover {
			.avatar-box {
				border-color: var(--im-color-primary, #409EFF);
			}

			.avatar-mask {
				opacity: 1;
			}
		}

		.avatar-uploader-icon {
			font-size: 24px;
			color: #8c939d;
			width: var(--width);
			height: var(--width);
			line-height: var(--width);
			text-align: center;
		}

		.avatar {
			width: var(--width);
			height: var(--width);
			display: block;
			object-fit: cover;
		}

		.avatar-mask {
			position: absolute;
			left: 0;
			bottom: 0;
			width: 100%;
			height: 40%;
			display: flex;
			align-items: center;
			justify-content: center;
			background: rgba(0, 0, 0, 0.45);
			color: #fff;
			font-size: 14px;
			opacity: 0;
			transition: opacity 0.2s;
		}
	}

	// 临时会话二维码面板
	.guest-qr-box {
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: 12px 0 4px;

		.guest-qr-loading {
			color: var(--im-text-color-light);
			font-size: 13px;
			padding: 24px 0;
		}

		.guest-qr-img {
			width: 180px;
			height: 180px;
			border-radius: 8px;
			border: 1px solid var(--im-border-color, #ebeef5);
		}

		.guest-qr-url {
			margin-top: 8px;
			font-size: 12px;
			color: var(--im-text-color-light);
			word-break: break-all;
			text-align: center;
			max-width: 360px;
		}

		.guest-qr-actions {
			margin-top: 10px;
		}

		.guest-qr-tip {
			margin-top: 8px;
			font-size: 12px;
			color: var(--im-text-color-lighter, #b8c2cc);
			text-align: center;
			max-width: 380px;
		}
	}
}
</style>

<!-- 非 scoped: el-dialog 内部元素由 Element UI 渲染在 body 层级, scoped 命中不到, 需全局样式 -->
<style lang="scss">
/* 设置弹窗内容较多(资料/偏好/安全/访客二维码/欢迎语): 将弹窗整体限制在视口内,
   头部与底部固定, 中间 body 内部滚动, 避免弹窗过高时顶部标题/关闭按钮被浏览器裁掉看不到。*/
.setting-dialog {
	display: flex;
	flex-direction: column;
	max-height: 86vh;
	margin-top: 7vh !important;
	margin-bottom: 0 !important;

	.el-dialog__header,
	.el-dialog__footer {
		flex-shrink: 0;
	}

	.el-dialog__body {
		flex: 1 1 auto;
		min-height: 0;
		overflow-y: auto;
		padding-top: 10px;
		padding-bottom: 10px;
	}
}
</style>
